import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { ArrowForward, Close, Search } from '@mui/icons-material';

import { Button } from '../../../../components/core';
import { useDebounce } from '../../../../hooks/useDebounce';
import { Alert } from '../../../Paylinks/components/Alert';
import { DataTable, Column } from '../../../../common/DataTable';
import { Pagination } from '../../../../common/DataTable/Pagination';
import { useMerchant } from '../../../../contexts/MerchantContext';
import { MerchantAccess } from '../../../../store/modules/merchant/types';
import type { PaginatedResponse } from '../../../../store/modules/types';
import { CONFIG_MERCHANT_LIST_PAGE_SIZE } from '../../../../util/constants';

const SEARCH_DEBOUNCE_MS = 500;

export const ParentMerchantsSelectionView = () => {
  const {
    getPaginatedAccountMerchants,
    selectParentAndLoadChildren,
    parentMerchants,
    parentMerchantsTotalRecords,
  } = useMerchant();

  const [searchInput, setSearchInput] = useState('');
  const [filterQuery, setFilterQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(CONFIG_MERCHANT_LIST_PAGE_SIZE);
  const [listResponse, setListResponse] = useState<PaginatedResponse<MerchantAccess> | null>(null);
  const [listLoading, setListLoading] = useState(true);
  const tableRef = useRef<HTMLDivElement>(null);

  const applyDebouncedFilter = useDebounce((value: string) => {
    setFilterQuery(value);
  }, SEARCH_DEBOUNCE_MS);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchInput(value);
      if (value === '') {
        setFilterQuery('');
      } else {
        applyDebouncedFilter(value);
      }
    },
    [applyDebouncedFilter],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    setFilterQuery('');
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filterQuery, pageSize]);

  useEffect(() => {
    let cancelled = false;

    /** Bootstrap already requested page 1 with CONFIG_MERCHANT_LIST_PAGE_SIZE — reuse context to avoid a duplicate network call. */
    const canSeedFromContext =
      page === 1 &&
      !filterQuery.trim() &&
      pageSize === CONFIG_MERCHANT_LIST_PAGE_SIZE &&
      parentMerchantsTotalRecords != null &&
      parentMerchants != null &&
      parentMerchants.length > 0;

    if (canSeedFromContext) {
      const totalPages = Math.max(1, Math.ceil(parentMerchantsTotalRecords / pageSize));
      setListResponse({
        data: parentMerchants,
        metadata: {
          totalRecords: parentMerchantsTotalRecords,
          totalPages,
          pageNumber: 1,
          pageSize,
          links: {
            previousPage: null,
            nextPage: null,
            firstPage: '',
            lastPage: '',
          },
        },
      });
      setListLoading(false);
      return;
    }

    (async () => {
      setListLoading(true);
      try {
        const res = await getPaginatedAccountMerchants({
          pageNumber: page,
          pageSize,
          onlyParentMerchants: true,
          ...(filterQuery.trim() ? { merchantName: filterQuery.trim() } : {}),
        });
        if (!cancelled) {
          setListResponse(res);
        }
      } catch {
        if (!cancelled) {
          setListResponse(null);
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    filterQuery,
    getPaginatedAccountMerchants,
    parentMerchants,
    parentMerchantsTotalRecords,
  ]);

  const pagedRows = listResponse?.data ?? [];
  const totalRecords = listResponse?.metadata.totalRecords ?? 0;
  const pageCount = Math.max(1, listResponse?.metadata.totalPages ?? 1);
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage]);

  const onContinue = useCallback(
    (m: MerchantAccess) => {
      selectParentAndLoadChildren(m);
    },
    [selectParentAndLoadChildren],
  );

  const columns: Column<MerchantAccess>[] = useMemo(
    () => [
      {
        headerName: 'Merchant name',
        field: 'name',
        minWidth: 280,
        renderCell: (row) => (
          <Typography variant="body2" color="text.primary">
            {row.name}
          </Typography>
        ),
      },
      {
        headerName: 'Actions',
        minWidth: 140,
        align: 'right',
        renderCell: (row) => (
          <Button
            theme="secondary"
            text="Continue"
            startIcon={<ArrowForward sx={{ fontSize: 18 }} />}
            onClick={() => onContinue(row)}
            sx={{
              height: 40,
              minHeight: 40,
              px: 2,
            }}
          />
        ),
      },
    ],
    [onContinue],
  );

  return (
    <Stack spacing={3}>
      <Alert severity="warning">
        <Stack spacing={1}>
          <Typography sx={{ fontSize: '14px' }}>
            You have access to multiple parent merchants. Select one of the parent merchants to
            continue.
          </Typography>
        </Stack>
      </Alert>

      <Stack gap={2}>
        <Typography
          component="h2"
          variant="xl"
          fontWeight="semibold"
          sx={{ fontSize: { xs: '16px', md: '20px' } }}
        >
          Parent merchants
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, width: '100%' }}>
          <TextField
            fullWidth
            label="Search parent merchants by name"
            value={searchInput}
            onChange={handleSearchChange}
            autoComplete="off"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: -0.5 }}>
                    {searchInput.trim() ? (
                      <IconButton
                        aria-label="Clear search"
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearSearch();
                        }}
                        sx={{
                          cursor: 'pointer',
                          color: 'text.secondary',
                          p: 0.75,
                        }}
                      >
                        <Close sx={{ fontSize: 20 }} />
                      </IconButton>
                    ) : null}
                    <IconButton
                      aria-hidden
                      edge="end"
                      tabIndex={-1}
                      disableRipple
                      onClick={(e) => {
                        const input = e.currentTarget
                          .closest('.MuiInputBase-root')
                          ?.querySelector('input');
                        input?.focus();
                      }}
                      sx={{
                        px: [1.5, 3.5],
                        backgroundColor: 'link.main',
                        color: 'link.contrastText',
                        py: 1,
                        borderRadius: '100px',
                        mr: -1.2,
                        cursor: 'pointer',
                      }}
                    >
                      <Search />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: 'background.default',
              borderRadius: '51px',
              '& input': {
                pr: 2,
                py: 1.5,
              },
              '& .MuiInputBase-root': {
                borderRadius: '51px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'grey.400',
                px: 2,
                fontSize: (theme) => theme.typography.pxToRem(17.7),
              },
              '& .MuiInputLabel-root': {
                ml: 1,
                lineHeight: '20px',
                color: 'text.primary',
                maxWidth: ['calc(100% - 80px)', 'calc(100% - 120px)'],
              },
            }}
          />
        </Box>
      </Stack>

      <DataTable<MerchantAccess>
        columns={columns}
        rows={pagedRows}
        getRowId={(row) => row.merchantId}
        isLoading={listLoading}
        pageSize={pageSize}
        tableHeight="min(480px, 60vh)"
        noRowsMessage="No parent merchants match your search."
        tableRef={tableRef}
        renderPagination={() => (
          <Pagination
            itemCount={pagedRows.length}
            itemTotal={totalRecords}
            pageCount={pageCount}
            onPageChange={(_, p) => {
              setPage(p);
              tableRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            page={safePage}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => {
              setPage(1);
              setPageSize(newPageSize);
            }}
            disabled={listLoading}
            pageSizeOptions={[10, 20, 30, 40, 50]}
          />
        )}
      />
    </Stack>
  );
};
